// User role types and constants
export type UserRole = "admin" | "mod" | "STUDENT" | "TEACHER";
export const ALL_ROLES: UserRole[] = ["admin", "mod", "STUDENT", "TEACHER"];

// Account type (account_type column) — matches AccountType enum in ai-tutor
export type AccountType = "user" | "demo" | "admin";
export const ALL_ACCOUNT_TYPES: AccountType[] = ["user", "demo", "admin"];
/**
 * The one type Users Config may set: `user`, for a demo account that leaves its
 * trial. `demo` comes from Manage trial, `admin` from Dashboard IAM.
 */
export type ConvertibleAccountType = Extract<AccountType, "user">;

// Account role (account_role column) — matches AccountRole enum in ai-tutor
export type AccountRole = "STUDENT" | "TEACHER" | "ORG_ADMIN";
export const ALL_ACCOUNT_ROLES: AccountRole[] = [
  "ORG_ADMIN",
  "TEACHER",
  "STUDENT",
];

export const ACCOUNT_ROLE_LABELS: Record<AccountRole, string> = {
  STUDENT: "Student",
  TEACHER: "Teacher",
  ORG_ADMIN: "Org Admin",
};
export const VALID_ROLES_SET = new Set<string>(ALL_ROLES);

export type UserAccount = {
  userId: string;
  providerId: string;
  createdAt?: string;
};

export type ManagedUser = {
  id: string;
  email: string;
  name?: string | null;
  username?: string | null;
  displayUsername?: string | null;
  role?: UserRole | UserRole[] | null;
  account_role?: AccountRole | null;
  account_type?: AccountType | null;
  createdAt?: string;
  banned?: boolean;
  banReason?: string | null;
  banExpiresAt?: string | null;
  // Auth providers (e.g., "credential", "google", "classlink")
  providers?: string[];
  organizationId?: string | null;
  organizationName?: string | null;
  teacherId?: number | null;
  coTeacherCount?: number;
  // Only present/meaningful when account_role is "STUDENT"; one of ALL_GRADES.
  grade?: string | null;
};

export type SearchField = "email" | "name";

export const PAGE_SIZE = 20;

export const roleVariants: Record<string, string> = {
  admin: "border border-lime-600/20 bg-lime-600/10 text-lime-900",
  mod: "border border-emerald-600/20 bg-emerald-600/10 text-emerald-900",
  student: "border border-border/60 bg-muted/30 text-foreground",
  teacher: "border border-lime-600/20 bg-lime-600/10 text-lime-900",
  org_admin: "border border-indigo-600/20 bg-indigo-600/10 text-indigo-900",
  demo: "border border-amber-600/30 bg-amber-500/10 text-amber-900",
  none: "border border-border/60 bg-muted/30 text-muted-foreground",
};

export const providerVariants: Record<string, string> = {
  credential: "border border-blue-600/20 bg-blue-600/10 text-blue-900",
  google: "border border-red-600/20 bg-red-600/10 text-red-900",
  classlink: "border border-purple-600/20 bg-purple-600/10 text-purple-900",
  default: "border border-gray-600/20 bg-gray-600/10 text-gray-900",
};

export const getProviderLabel = (providerId: string): string => {
  const labels: Record<string, string> = {
    credential: "Password",
    google: "Google",
    classlink: "ClassLink",
  };
  return labels[providerId] || providerId;
};

// Form data for creating new users
export type CreateUserFormData = {
  name: string;
  email: string;
  password: string;
  username: string;
  // Public-facing display name; falls back to the username if left blank.
  displayUsername: string;
  account_role: AccountRole;
  organizationId?: string;
  // Free-text org name typed for an Org Admin (creates a new org they manage).
  organizationName?: string;
  // Co-teachers pre-selected for a new Teacher (persisted after the teacher
  // row is created). teacher_id values from the selected organization.
  coTeacherIds?: number[];
  // Only meaningful when account_role is "STUDENT"; one of ALL_GRADES.
  grade?: string;
};

// Form data for editing existing users
export type EditUserFormData = {
  name: string;
  email: string;
  username: string;
  displayUsername: string;
  organizationId?: string;
  // Only meaningful when the user being edited is a STUDENT.
  grade?: string;
};

// User summary statistics
export type UserSummary = {
  banned: number;
};

/**
 * Normalize and dedupe roles into a valid UserRole array.
 * Filters out invalid roles and removes duplicates while preserving case.
 */
export const normalizeRoles = (roles: (string | UserRole)[]): UserRole[] => {
  const seen = new Set<string>();
  const result: UserRole[] = [];
  for (const role of roles) {
    if (VALID_ROLES_SET.has(role) && !seen.has(role)) {
      seen.add(role);
      result.push(role as UserRole);
    }
  }
  return result;
};

// account_type is the admin gate; mod was collapsed into admin by migration 0037.
export const isAdminRole = (accountType: ManagedUser["account_type"]): boolean =>
  accountType === "admin";

export const isUserBanned = (user: ManagedUser): boolean =>
  Boolean(user.banned || user.banExpiresAt);

/**
 * Whether the support-login action should be offered for this user.
 *
 * Presentation only. The real gate is server-side: et-auth refuses admin
 * targets on both the issue and the redeem paths, and admins are the only
 * accounts that hold dashboard tabs. This just avoids offering a menu item
 * whose only outcome is a refusal.
 */
export const canOfferSupportLogin = (user: ManagedUser): boolean =>
  !isAdminRole(user.account_type);
